import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, CURRENCY_OPTIONS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, Plus, Users, TrendingUp, DollarSign, Gift, Trash2, Pencil, Copy, ExternalLink,
  Layers, X, Package, Wallet, Upload, FileDown, Trash, ImageIcon, AlertTriangle,
} from "lucide-react";

const tabBtn = "rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold px-4";

function Stat({ icon: Icon, label, value, sub, bg }) {
  return (
    <div className={`card-pop ${bg} p-6`} data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2"><Icon className="w-5 h-5" /><span className="eyebrow">{label}</span></div>
      <p className="font-display text-3xl font-extrabold">{value}</p>
      {sub && <p className="text-slate-600 text-xs mt-1">{sub}</p>}
    </div>
  );
}

const emptyTier = { name: "", description: "", price: "", benefits: [""], active: true };
const emptyProd = { id: null, name: "", description: "", price: "", thumbnail_url: "", file: null };

export default function CreatorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [payoutReqs, setPayoutReqs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [products, setProducts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [tierDialog, setTierDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState(emptyTier);
  const [savingTier, setSavingTier] = useState(false);

  const [prodDialog, setProdDialog] = useState(false);
  const [prodForm, setProdForm] = useState(emptyProd);
  const [savingProd, setSavingProd] = useState(false);

  const [requestingPayout, setRequestingPayout] = useState(false);
  const [connect, setConnect] = useState(null);

  const load = async () => {
    try {
      const [s, po, pr2, t, pr, sub, p, cn] = await Promise.all([
        api.get("/creator/stats"), api.get("/creator/payouts"), api.get("/creator/payout-requests"),
        api.get("/tiers/mine"), api.get("/products/mine"), api.get("/creator/subscribers"), api.get("/creators/me"),
        api.get("/creator/connect/status"),
      ]);
      setStats(s.data); setPayouts(po.data); setPayoutReqs(pr2.data);
      setTiers(t.data); setProducts(pr.data); setSubs(sub.data); setProfile(p.data); setConnect(cn.data);
    } catch (e) {
      toast.error("Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const connectStripe = async () => {
    try { const { data } = await api.post("/creator/connect"); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start Stripe setup."); }
  };

  const cur = stats?.currency;
  const storefrontUrl = profile ? `${window.location.origin}/s/${profile.handle}` : "";
  const copyLink = () => { navigator.clipboard.writeText(storefrontUrl); toast.success("Storefront link copied!"); };

  const openNewTier = () => { setEditingTier(null); setTierForm(emptyTier); setTierDialog(true); };
  const openEditTier = (t) => { setEditingTier(t); setTierForm({ name: t.name, description: t.description, price: String(t.price), benefits: t.benefits.length ? t.benefits : [""], active: t.active }); setTierDialog(true); };

  const saveTier = async () => {
    if (!tierForm.name || !tierForm.price || Number(tierForm.price) <= 0) { toast.error("Add a name and valid price."); return; }
    setSavingTier(true);
    const payload = { name: tierForm.name, description: tierForm.description, price: Number(tierForm.price), benefits: tierForm.benefits.filter((b) => b.trim()), active: tierForm.active };
    try {
      if (editingTier) { await api.put(`/tiers/${editingTier.id}`, payload); toast.success("Tier updated!"); }
      else { await api.post("/tiers", payload); toast.success("Tier created!"); }
      setTierDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save tier."); }
    finally { setSavingTier(false); }
  };

  const deleteTier = async (id) => { try { await api.delete(`/tiers/${id}`); toast.success("Tier archived."); load(); } catch (e) { toast.error("Failed."); } };

  const openNewProd = () => { setProdForm(emptyProd); setProdDialog(true); };
  const openEditProd = (p) => { setProdForm({ id: p.id, name: p.name, description: p.description, price: String(p.price), thumbnail_url: p.thumbnail_url, file: null }); setProdDialog(true); };

  const saveProduct = async () => {
    if (!prodForm.name || !prodForm.price || Number(prodForm.price) <= 0) { toast.error("Add a name and valid price."); return; }
    if (!prodForm.id && !prodForm.file) { toast.error("Please attach a file to sell."); return; }
    setSavingProd(true);
    const fd = new FormData();
    fd.append("name", prodForm.name);
    fd.append("description", prodForm.description);
    fd.append("price", String(Number(prodForm.price)));
    fd.append("thumbnail_url", prodForm.thumbnail_url);
    if (prodForm.file) fd.append("file", prodForm.file);
    try {
      if (prodForm.id) { await api.put(`/products/${prodForm.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Product updated!"); }
      else { await api.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Product published!"); }
      setProdDialog(false); setProdForm(emptyProd); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save product."); }
    finally { setSavingProd(false); }
  };

  const deleteProduct = async (id) => { try { await api.delete(`/products/${id}`); toast.success("Product archived."); load(); } catch (e) { toast.error("Failed."); } };

  const requestPayout = async () => {
    if (!payouts || payouts.net_payout <= 0) { toast.info("No balance available to pay out yet."); return; }
    setRequestingPayout(true);
    try {
      await api.post("/creator/payout-request", { amount: payouts.net_payout });
      toast.success("Payout requested! Our accounts team will process it.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to request payout."); }
    finally { setRequestingPayout(false); }
  };

  const setBenefit = (i, val) => { const b = [...tierForm.benefits]; b[i] = val; setTierForm({ ...tierForm, benefits: b }); };

  if (loading) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="w-8 h-8 text-violet-600 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#111114]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow text-violet-600 mb-1">Creator Studio</p>
            <h1 className="font-display text-3xl font-extrabold">Hey, {profile?.display_name || user?.name} 👋</h1>
          </div>
          <div className="card-pop bg-white px-3 py-2 flex items-center gap-2 text-sm">
            <span className="text-slate-600 font-mono truncate max-w-[180px]">/s/{profile?.handle}</span>
            <button onClick={copyLink} className="w-8 h-8 rounded-lg border-2 border-[#111114] bg-lime-200 flex items-center justify-center hover:bg-lime-300" data-testid="copy-storefront-link"><Copy className="w-4 h-4" /></button>
            <a href={storefrontUrl} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg border-2 border-[#111114] bg-cyan-200 flex items-center justify-center hover:bg-cyan-300" data-testid="view-storefront"><ExternalLink className="w-4 h-4" /></a>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          <Stat icon={Users} label="Active subscribers" value={stats.active_subscribers} bg="bg-violet-200" />
          <Stat icon={DollarSign} label="MRR" value={money(stats.mrr, cur)} sub="Monthly recurring" bg="bg-lime-200" />
          <Stat icon={TrendingUp} label="ARR" value={money(stats.arr, cur)} sub="Annual run rate" bg="bg-cyan-200" />
          <Stat icon={Package} label="Product sales" value={money(stats.product_revenue, cur)} bg="bg-amber-200" />
        </div>

        <div className="card-pop bg-white p-6 mb-8" data-testid="fee-status-widget">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1"><Gift className="w-5 h-5 text-violet-600" /><h3 className="font-display text-lg font-extrabold">Platform fee — {stats.fee_pct}%</h3></div>
              <p className="text-slate-600 text-sm">A flat {stats.fee_pct}% fee applies to all subscriptions &amp; product sales. Payouts via PayPal, every 2 weeks on a Friday (NZST).</p>
            </div>
            <div className="text-right">
              <p className="font-display text-3xl font-extrabold" data-testid="fee-owed">{money(stats.platform_fee_owed, cur)}</p>
              <p className="text-slate-500 text-xs">est. fee on {money(stats.mrr + stats.product_revenue, cur)}</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="tiers">
          <TabsList className="bg-white border-2 border-[#111114] rounded-xl p-1 mb-6 h-auto flex-wrap">
            <TabsTrigger value="tiers" className={tabBtn} data-testid="tab-tiers">Tiers</TabsTrigger>
            <TabsTrigger value="products" className={tabBtn} data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="subscribers" className={tabBtn} data-testid="tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="payouts" className={tabBtn} data-testid="tab-payouts">Payouts</TabsTrigger>
            <TabsTrigger value="storefront" className={tabBtn} data-testid="tab-storefront">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="tiers">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display text-xl font-extrabold">Membership tiers</h2>
              <Button onClick={openNewTier} className="bg-violet-600 text-white btn-pop rounded-full font-bold" data-testid="new-tier-btn"><Plus className="w-4 h-4 mr-2" /> New tier</Button>
            </div>
            {tiers.length === 0 ? (
              <div className="card-pop bg-white p-12 text-center"><Layers className="w-10 h-10 text-slate-400 mx-auto mb-3" /><p className="text-slate-600">No tiers yet. Create your first membership tier.</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tiers.map((t) => (
                  <div key={t.id} className={`card-pop bg-white p-6 ${!t.active ? "opacity-50" : ""}`} data-testid={`creator-tier-${t.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-display text-lg font-extrabold">{t.name}</h3>
                        <p className="font-display text-2xl font-extrabold text-violet-600">{money(t.price, t.currency)}<span className="text-sm text-slate-500 font-normal">/mo</span></p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openEditTier(t)} className="w-8 h-8 rounded-lg border-2 border-[#111114] bg-white flex items-center justify-center hover:bg-violet-100" data-testid={`edit-tier-${t.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteTier(t.id)} className="w-8 h-8 rounded-lg border-2 border-[#111114] bg-white flex items-center justify-center hover:bg-rose-100" data-testid={`delete-tier-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    {t.description && <p className="text-slate-600 text-sm mb-3">{t.description}</p>}
                    <p className="text-slate-500 text-xs font-mono">{t.subscriber_count} subscribers · {t.active ? "Live" : "Archived"}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="products">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display text-xl font-extrabold">Digital products</h2>
              <Button onClick={openNewProd} className="bg-violet-600 text-white btn-pop rounded-full font-bold" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-2" /> New product</Button>
            </div>
            {products.length === 0 ? (
              <div className="card-pop bg-white p-12 text-center"><Package className="w-10 h-10 text-slate-400 mx-auto mb-3" /><p className="text-slate-600">No products yet. Upload a file and start selling downloads.</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((p) => (
                  <div key={p.id} className={`card-pop bg-white overflow-hidden ${!p.active ? "opacity-50" : ""}`} data-testid={`creator-product-${p.id}`}>
                    <div className="h-28 bg-cover bg-center border-b-2 border-[#111114] relative" style={{ backgroundImage: `url(${p.thumbnail_url || "https://images.unsplash.com/photo-1554056878-6911635b1573?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"})` }}>
                      <span className="chip bg-cyan-200 absolute top-2 left-2">{p.file_type}</span>
                    </div>
                    <div className="p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-display font-extrabold">{p.name}</h3>
                          <p className="font-display text-xl font-extrabold text-violet-600">{money(p.price, p.currency)}</p>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openEditProd(p)} className="w-8 h-8 rounded-lg border-2 border-[#111114] bg-white flex items-center justify-center hover:bg-violet-100" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => deleteProduct(p.id)} className="w-8 h-8 rounded-lg border-2 border-[#111114] bg-white flex items-center justify-center hover:bg-rose-100" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <p className="text-slate-500 text-xs font-mono mt-2 flex items-center gap-1"><FileDown className="w-3 h-3" /> {p.download_count} downloads</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="subscribers">
            <div className="card-pop bg-white overflow-hidden">
              {subs.length === 0 ? (
                <div className="p-12 text-center"><Users className="w-10 h-10 text-slate-400 mx-auto mb-3" /><p className="text-slate-600">No subscribers yet. Share your storefront link!</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b-2 border-[#111114] text-left font-mono text-xs uppercase">
                      <th className="px-6 py-4">Member</th><th className="px-6 py-4">Tier</th><th className="px-6 py-4">Amount</th><th className="px-6 py-4">Fee</th><th className="px-6 py-4">Status</th>
                    </tr></thead>
                    <tbody>
                      {subs.map((s) => (
                        <tr key={s.id} className="border-b border-slate-200 hover:bg-violet-50" data-testid={`subscriber-row-${s.id}`}>
                          <td className="px-6 py-4 flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg border-2 border-[#111114] bg-violet-200 flex items-center justify-center overflow-hidden shrink-0">
                              {s.member_avatar ? <img src={s.member_avatar} alt="" className="w-full h-full object-cover" /> : <span className="font-bold text-xs">{(s.member_name || "?")[0]}</span>}
                            </div>
                            <span className="font-semibold">{s.member_name}</span>
                          </td>
                          <td className="px-6 py-4 text-slate-600">{s.tier_name}</td>
                          <td className="px-6 py-4 font-mono font-bold">{money(s.amount, s.currency)}</td>
                          <td className="px-6 py-4">{s.counts_as_free ? <span className="chip bg-lime-200">Free</span> : <span className="chip bg-amber-200">{money(s.platform_fee, s.currency)}</span>}</td>
                          <td className="px-6 py-4"><span className={`chip ${s.status === "active" ? "bg-lime-300" : "bg-rose-300"}`}>{s.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payouts">
            <div className="card-pop bg-amber-100 border-2 border-[#111114] p-5 mb-6 flex items-start gap-3" data-testid="payout-schedule-note">
              <Wallet className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <p className="text-sm font-semibold text-[#111114] leading-relaxed">
                Payouts are processed only on a <strong>Friday at 12:00am NZST</strong>. Requests made after 12:00am will be processed the next business day (<strong>Monday at 12:00am</strong>). Payments are processed every 2 weeks on <strong>Mondays and Fridays</strong>.
              </p>
            </div>
            {connect && !connect.payouts_ready && (
              <div className="card-pop bg-white border-2 border-[#111114] p-5 mb-6" data-testid="stripe-connect-card">
                <div className="flex items-center gap-2 mb-2"><Wallet className="w-5 h-5 text-violet-600" /><h3 className="font-display text-lg font-extrabold">{connect.connected ? "Finish your Stripe payout setup" : "Set up Stripe payouts"}</h3></div>
                <p className="text-slate-600 text-sm mb-4">{connect.connected ? "Your Stripe account needs a few more details before you can receive payouts." : "Connect a Stripe account to receive your payouts securely — takes about 2 minutes."}</p>
                <Button onClick={connectStripe} className="bg-violet-600 text-white btn-pop rounded-full font-bold" data-testid="stripe-connect-btn">{connect.connected ? "Finish Stripe setup" : "Set up Stripe payouts"}</Button>
              </div>
            )}
            {connect && connect.payouts_ready && (
              <div className="card-pop bg-lime-200 border-2 border-[#111114] p-4 mb-6 flex items-center gap-2" data-testid="stripe-connected-note">
                <Wallet className="w-5 h-5 text-[#111114]" /><p className="font-semibold text-[#111114]">Stripe connected — payouts enabled. You're ready to request payouts.</p>
              </div>
            )}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="card-pop bg-white p-7">
                <div className="flex items-center gap-2 mb-6"><Wallet className="w-6 h-6 text-violet-600" /><h2 className="font-display text-xl font-extrabold">This month's payout</h2></div>
                <div className="space-y-3">
                  <Row label="Subscription revenue" value={money(payouts.gross_subscription_revenue, cur)} />
                  <Row label="Product sales" value={money(payouts.product_revenue, cur)} />
                  <Row label="Gross earnings" value={money(payouts.gross_total, cur)} bold />
                  <Row label={`Platform fee (${payouts.fee_pct}%)`} value={`- ${money(payouts.platform_fee, cur)}`} neg />
                  <Row label="Payout fee" value={`- ${money(payouts.payout_fee, cur)}`} neg />
                  <div className="border-t-2 border-dashed border-[#111114] my-2" />
                  <div className="flex justify-between items-center rounded-xl bg-lime-200 border-2 border-[#111114] p-4">
                    <span className="font-display font-extrabold">Net payout</span>
                    <span className="font-display text-2xl font-extrabold" data-testid="net-payout">{money(payouts.net_payout, cur)}</span>
                  </div>
                  <Button onClick={requestPayout} disabled={requestingPayout || payouts.net_payout <= 0 || !connect?.payouts_ready} className="w-full bg-violet-600 text-white btn-pop rounded-full font-bold mt-2" data-testid="request-payout-btn">
                    {requestingPayout ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4 mr-2" /> Request payout</>}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">{payouts.payout_schedule}{connect?.payouts_ready ? " Paid to your connected Stripe account." : " Connect your Stripe account above first."}</p>
                </div>
              </div>
              <div>
                <div className="card-pop bg-violet-600 text-white p-7 mb-6">
                  <Wallet className="w-10 h-10 text-lime-300 mb-4" />
                  <h3 className="font-display text-2xl font-extrabold mb-2">Stripe payouts, every 2 weeks</h3>
                  <p className="text-violet-100 leading-relaxed">Payouts are processed only on a Friday at 12:00am NZST. Requests made after 12:00am are processed the next business day (Monday at 12:00am). Payments run every 2 weeks on Mondays &amp; Fridays. A flat {payouts.fee_pct}% platform fee plus a {money(payouts.payout_fee, cur)} payout fee applies.</p>
                </div>
                <div className="card-pop bg-white p-6">
                  <h3 className="font-display text-lg font-extrabold mb-4">Payout requests</h3>
                  {payoutReqs.length === 0 ? <p className="text-slate-500 text-sm">No payout requests yet.</p> : (
                    <div className="space-y-2">
                      {payoutReqs.map((r) => (
                        <div key={r.id} className="flex items-center justify-between rounded-xl border-2 border-[#111114] p-3" data-testid={`payout-req-${r.id}`}>
                          <div>
                            <p className="font-display font-extrabold">{money(r.amount, r.currency)}</p>
                            <p className="text-xs text-slate-500 font-mono">{new Date(r.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className="chip bg-amber-200 capitalize">{r.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="storefront">
            <ProfileEditor profile={profile} onSaved={load} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Tier dialog */}
      <Dialog open={tierDialog} onOpenChange={setTierDialog}>
        <DialogContent className="bg-white border-2 border-[#111114] rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editingTier ? "Edit tier" : "Create a new tier"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Tier name" value={tierForm.name} onChange={(v) => setTierForm({ ...tierForm, name: v })} placeholder="e.g. Gold Membership" testid="tier-name-input" />
            <Field label={`Monthly price (${profile?.currency?.toUpperCase() || "NZD"})`} type="number" value={tierForm.price} onChange={(v) => setTierForm({ ...tierForm, price: v })} placeholder="9.99" testid="tier-price-input" />
            <div><Label className="text-sm font-semibold mb-1.5 block">Description</Label><Textarea value={tierForm.description} onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })} className="border-2 border-[#111114] rounded-xl" data-testid="tier-desc-input" /></div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Benefits</Label>
              <div className="space-y-2">
                {tierForm.benefits.map((b, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={b} onChange={(e) => setBenefit(i, e.target.value)} placeholder={`Benefit ${i + 1}`} className="border-2 border-[#111114] rounded-xl" data-testid={`tier-benefit-${i}`} />
                    {tierForm.benefits.length > 1 && <button onClick={() => setTierForm({ ...tierForm, benefits: tierForm.benefits.filter((_, x) => x !== i) })} className="p-2 text-slate-500 hover:text-rose-500"><X className="w-4 h-4" /></button>}
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setTierForm({ ...tierForm, benefits: [...tierForm.benefits, ""] })} className="text-violet-600 hover:bg-violet-50 font-semibold" data-testid="add-benefit-btn"><Plus className="w-4 h-4 mr-1" /> Add benefit</Button>
              </div>
            </div>
            <div className="flex items-center justify-between"><Label className="text-sm font-semibold">Live on storefront</Label><Switch checked={tierForm.active} onCheckedChange={(v) => setTierForm({ ...tierForm, active: v })} data-testid="tier-active-switch" /></div>
          </div>
          <DialogFooter><Button onClick={saveTier} disabled={savingTier} className="bg-violet-600 text-white btn-pop rounded-full font-bold w-full" data-testid="save-tier-btn">{savingTier ? <Loader2 className="w-4 h-4 animate-spin" /> : editingTier ? "Save changes" : "Create tier"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product dialog */}
      <Dialog open={prodDialog} onOpenChange={setProdDialog}>
        <DialogContent className="bg-white border-2 border-[#111114] rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{prodForm.id ? "Edit product" : "New digital product"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Product name" value={prodForm.name} onChange={(v) => setProdForm({ ...prodForm, name: v })} placeholder="e.g. Lightroom Preset Pack" testid="product-name-input" />
            <Field label={`Price (${profile?.currency?.toUpperCase() || "NZD"})`} type="number" value={prodForm.price} onChange={(v) => setProdForm({ ...prodForm, price: v })} placeholder="12.00" testid="product-price-input" />
            <div><Label className="text-sm font-semibold mb-1.5 block">Description</Label><Textarea value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} className="border-2 border-[#111114] rounded-xl" data-testid="product-desc-input" /></div>
            <Field label="Thumbnail image URL (optional)" value={prodForm.thumbnail_url} onChange={(v) => setProdForm({ ...prodForm, thumbnail_url: v })} placeholder="https://..." testid="product-thumb-input" />
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">{prodForm.id ? "Replace file (optional)" : "File to sell"}</Label>
              <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-[#111114] p-4 cursor-pointer hover:bg-violet-50" data-testid="product-file-label">
                <div className="w-10 h-10 rounded-lg bg-violet-200 border-2 border-[#111114] flex items-center justify-center"><Upload className="w-5 h-5" /></div>
                <span className="text-sm font-medium truncate">{prodForm.file ? prodForm.file.name : (prodForm.id ? "Keep current file, or click to replace" : "Click to choose a file (PDF, ZIP, image, audio…)")}</span>
                <input type="file" className="hidden" onChange={(e) => setProdForm({ ...prodForm, file: e.target.files[0] })} data-testid="product-file-input" />
              </label>
            </div>
          </div>
          <DialogFooter><Button onClick={saveProduct} disabled={savingProd} className="bg-violet-600 text-white btn-pop rounded-full font-bold w-full" data-testid="save-product-btn">{savingProd ? <Loader2 className="w-4 h-4 animate-spin" /> : prodForm.id ? "Save changes" : "Publish product"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold, neg }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-sm ${bold ? "font-bold text-[#111114]" : "text-slate-600"}`}>{label}</span>
      <span className={`font-mono ${bold ? "font-bold" : ""} ${neg ? "text-rose-600" : "text-[#111114]"}`}>{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label className="text-sm font-semibold mb-1.5 block">{label}</Label>
      <Input type={type} step={type === "number" ? "0.01" : undefined} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="border-2 border-[#111114] rounded-xl" data-testid={testid} />
    </div>
  );
}

function ImageUpload({ label, kind, url, onUploaded, testid, aspect }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      const { data } = await api.post("/creators/me/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success(`${label} updated!`);
    } catch (e) { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-sm font-semibold mb-1.5 block">{label}</Label>
      <div className="flex items-center gap-4">
        <div className={`${aspect} rounded-xl border-2 border-[#111114] overflow-hidden bg-violet-100 flex items-center justify-center shrink-0`}>
          {url ? <img src={url} alt={label} className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-400" />}
        </div>
        <label className="rounded-full border-2 border-[#111114] bg-white px-4 py-2 text-sm font-bold cursor-pointer hover:bg-violet-50 flex items-center gap-2" data-testid={testid}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        </label>
      </div>
    </div>
  );
}

function ProfileEditor({ profile, onSaved }) {
  const { refresh, logout } = useAuth();
  const [form, setForm] = useState({
    display_name: profile?.display_name || "", handle: profile?.handle || "", bio: profile?.bio || "",
    category: profile?.category || "", avatar_url: profile?.avatar_url || "", banner_url: profile?.banner_url || "",
    currency: profile?.currency || "nzd",
  });
  const [saving, setSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/creators/me", form);
      toast.success("Profile updated!");
      refresh(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update."); }
    finally { setSaving(false); }
  };

  const requestDeletion = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete-request", { reason: delReason });
      toast.success("Deletion request received. Signing you out.");
      setTimeout(() => logout(), 1500);
    } catch (e) { toast.error("Failed to submit request."); setDeleting(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="card-pop bg-white p-7">
        <h2 className="font-display text-xl font-extrabold mb-5">Profile & storefront</h2>
        <div className="space-y-5">
          <ImageUpload label="Profile picture" kind="avatar" url={form.avatar_url} onUploaded={(u) => { setForm({ ...form, avatar_url: u }); refresh(); onSaved(); }} testid="upload-avatar-btn" aspect="w-20 h-20" />
          <ImageUpload label="Background banner" kind="banner" url={form.banner_url} onUploaded={(u) => { setForm({ ...form, banner_url: u }); refresh(); onSaved(); }} testid="upload-banner-btn" aspect="w-32 h-20" />
          <Field label="Display name" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} testid="profile-name" />
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Handle (your link)</Label>
            <div className="flex items-center gap-1"><span className="text-slate-500 font-mono text-sm">/s/</span><Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="border-2 border-[#111114] rounded-xl" data-testid="profile-handle" /></div>
          </div>
          <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="e.g. Tech, Fitness, Art" testid="profile-category" />
          <div><Label className="text-sm font-semibold mb-1.5 block">Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell your community who you are…" className="border-2 border-[#111114] rounded-xl" data-testid="profile-bio" /></div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Payout currency <span className="text-slate-400 font-normal">(advanced — applies to new tiers & products)</span></Label>
            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full border-2 border-[#111114] rounded-xl h-11 px-3 bg-white font-medium" data-testid="profile-currency">
              {CURRENCY_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </div>
          <Button onClick={save} disabled={saving} className="bg-violet-600 text-white btn-pop rounded-full font-bold" data-testid="save-profile-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save profile"}</Button>
        </div>
      </div>

      <div className="card-pop bg-white p-7 border-rose-400" style={{ borderColor: "#f43f5e" }}>
        <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-rose-500" /><h2 className="font-display text-lg font-extrabold">Danger zone</h2></div>
        <p className="text-slate-600 text-sm mb-4">Request permanent deletion of your account and data. Our accounts team (accounts@vangstechsubs.com) processes requests.</p>
        <Button onClick={() => setDelOpen(true)} className="bg-white text-rose-600 rounded-full font-bold border-2 border-rose-500 hover:bg-rose-50" data-testid="request-delete-btn"><Trash className="w-4 h-4 mr-2" /> Request account deletion</Button>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-white border-2 border-[#111114] rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-500" /> Request account deletion</DialogTitle></DialogHeader>
          <p className="text-slate-600 text-sm">This submits a deletion request to Vanguard Tech Limited's accounts team. You'll be signed out. Tell us why (optional):</p>
          <Textarea value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="Reason (optional)" className="border-2 border-[#111114] rounded-xl" data-testid="delete-reason" />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setDelOpen(false)} className="bg-white text-[#111114] btn-pop rounded-full font-bold" data-testid="delete-cancel">Keep my account</Button>
            <Button onClick={requestDeletion} disabled={deleting} className="bg-rose-500 text-white btn-pop rounded-full font-bold hover:bg-rose-400" data-testid="delete-confirm">{deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
